import { NgModule, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router, RouterModule, Routes, UrlSerializer } from '@angular/router';
import { PageComponent } from './page.component';
import { LangGuard } from '../core/shared/guards/lang.guard';
import { DataPreloaderService } from '../core/services/data-preloader.service';
import { GamePageGuard } from '../core/shared/guards/game-page.guard';
import { AuthGuard } from '../core/shared/guards/auth.guard';
import { CanDeactivateGuard } from '../core/shared/guards/register-leave.guard';
import { NotAuthGuard } from '../core/shared/guards/not-auth.guard';
import { GameCategory } from '../core/services/games/game-category';
import { PlatformService } from '../core/services/platform.service';

const confirmationRedirects: Routes = [
  { path: 'users/auth/telegram', redirectTo: '/' },
  { path: ':lang/users/auth/telegram', redirectTo: '/' },
  { path: 'users/confirmation', redirectTo: 'confirmation/user' },
  { path: ':lang/users/confirmation', redirectTo: 'confirmation/user' },
  { path: 'reset-password', redirectTo: 'confirmation/password_edit' },
  {
    path: ':lang/reset-password',
    redirectTo: 'confirmation/password_edit',
  },
  { path: 'profile/limits/confirm', redirectTo: 'confirmation/limit' },
  { path: ':lang/profile/limits/confirm', redirectTo: 'confirmation/limit' },
  {
    path: 'profile/auth_providers/confirm',
    redirectTo: 'confirmation/auth_provider',
  },
  {
    path: ':lang/profile/auth_providers/confirm',
    redirectTo: 'confirmation/auth_provider',
  },
  {
    path: 'confirm-email',
    redirectTo: 'confirmation/email',
  },
  {
    path: ':lang/confirm-email',
    redirectTo: 'confirmation/email',
  },
  { path: 'users/unlock', redirectTo: 'confirmation/unlock' },
  { path: ':lang/users/unlock', redirectTo: 'confirmation/unlock' },
  { path: ':lang/users/sign_in', redirectTo: 'login' },
  { path: 'profile/limits', redirectTo: 'account/limits' },
  { path: ':lang/profile/limits', redirectTo: 'account/limits' },
  { path: 'deposit', redirectTo: 'account/deposit' },
  { path: ':lang/deposit', redirectTo: 'account/deposit' },
  { path: 'cashout', redirectTo: 'account/cashout' },
  { path: ':lang/cashout', redirectTo: 'account/cashout' },
];

const routes: Routes = [
  {
    path: '',
    component: PageComponent,
    resolve: {
      data: DataPreloaderService,
    },
    children: [
      {
        path: '',
        loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
        pathMatch: 'full',
        canActivate: [LangGuard],
      },
      {
        path: '404',
        loadChildren: () => import('./not-found/not-found.module').then(m => m.NotFoundModule),
      },
      {
        path: '403',
        loadChildren: () => import('./forbidden-page/forbidden-page.module').then(m => m.ForbiddenPageModule),
      },
      {
        path: 'transfer',
        loadChildren: () => import('./transfer/transfer.module').then(m => m.TransferModule),
      },
      ...confirmationRedirects,
      {
        path: ':lang',
        loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/login',
        loadChildren: () => import('./sign-in-page/sign-in-page.module').then(m => m.SignInPageModule),
        canActivate: [LangGuard, NotAuthGuard],
      },
      {
        path: ':lang/register',
        loadChildren: () => import('./sign-up-page/sign-up-page.module').then(m => m.SignUpPageModule),
        canActivate: [LangGuard, NotAuthGuard],
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: ':lang/recovery-password',
        loadChildren: () => import('./recovery-password/recovery-password.module').then(m => m.RecoveryPasswordModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/games/favorite',
        loadChildren: () => import('./extra-category-page/extra-category-page.module').then(m => m.ExtraCategoryPageModule),
        data: { category: GameCategory.FAVORITE },
        canActivate: [LangGuard, AuthGuard],
      },
      {
        path: ':lang/games/last-played',
        loadChildren: () => import('./extra-category-page/extra-category-page.module').then(m => m.ExtraCategoryPageModule),
        data: { category: GameCategory.LAST_PLAYED },
        canActivate: [LangGuard, AuthGuard],
      },
      {
        path: ':lang/games',
        loadChildren: () => import('./games-category/games-category.module').then(m => m.GamesCategoryModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/play/:slug',
        loadChildren: () => import('./game-page/game-page.module').then(m => m.GamePageModule),
        canActivate: [LangGuard, GamePageGuard],
      },
      {
        path: ':lang/account',
        loadChildren: () => import('./account/account.module').then(m => m.AccountModule),
        canActivate: [LangGuard, AuthGuard],
      },
      {
        path: ':lang/banking',
        loadChildren: () => import('./banking/banking.module').then(m => m.BankingModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/bonuses',
        loadChildren: () => import('./bonuses/bonuses.module').then(m => m.BonusesModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/tournaments',
        loadChildren: () => import('./tournaments/tournaments.module').then(m => m.TournamentsModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/about',
        loadChildren: () => import('./about-us/about-us.module').then(m => m.AboutUsModule),
        canActivate: [LangGuard],
      },
      // {
      //   path: ':lang/loyalty',
      //   loadChildren: () =>
      //     import('./loyalty/loyalty.page.module').then(
      //       (m) => m.LoyaltyPageModule
      //     ),
      //   canActivate: [LangGuard],
      // },
      {
        path: ':lang/mobile-app',
        loadChildren: () => import('./mobile-app/mobile-app.module').then(m => m.MobileAppModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/confirmation',
        loadChildren: () => import('./confirmation/confirmation.module').then(m => m.ConfirmationModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/contacts',
        loadChildren: () => import('./contacts/contacts.module').then(m => m.ContactsModule),
        canActivate: [LangGuard],
      },
      {
        path: 'payments-status',
        loadChildren: () => import('./payments-status/payments-status.module').then(m => m.PaymentsStatusModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/blog',
        loadChildren: () => import('./blog/blog.module').then(m => m.BlogModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/payments-status',
        loadChildren: () => import('./payments-status/payments-status.module').then(m => m.PaymentsStatusModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/:slug',
        loadChildren: () => import('./static-page/static-page.module').then(m => m.StaticPageModule),
        canActivate: [LangGuard],
      },
      {
        path: ':lang/promo',
        loadChildren: () => import('./promo/promo.module').then(m => m.PromoModule),
        canActivate: [LangGuard],
      },
      { path: '**', redirectTo: '404' },
    ],
  },
];

@NgModule({
  declarations: [],
  imports: [CommonModule, RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class PageRoutingModule {
  private _router = inject(Router);
  private _serializer = inject(UrlSerializer);
  private _platform = inject(PlatformService);
  private _location = inject(Location);

  constructor() {
    this._detectNoCacheParameter();
  }
  private _detectNoCacheParameter() {
    if (!this._platform.isBrowser) {
      return;
    }

    const params = this._serializer.parse(window.location.search).queryParams;
    setTimeout(() => {
      if (params.hasOwnProperty('nocache')) {
        if (params.hasOwnProperty('nocache')) {
          delete params.nocache;
        }
        const tree = this._router.parseUrl(this._location.normalize(window.location.pathname));
        tree.queryParams = params;
        this._router.navigateByUrl(tree).then();
      }
    }, 100);
  }
}
